/* eslint-disable @typescript-eslint/no-unused-vars */
/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Container,
    SimpleGrid,
    Skeleton as ChakraSkeleton,
    Stack,
    Button,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '../../components/toggle-card'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import EmployeeBalance from './oms-employee-balance'
import {useGigya} from '../../context/gigya'
import {remCalc} from '../../theme/_utils'
import useCustomApi from '../../hooks/use-custom-api'
import {useWishList} from '../../hooks/use-wish-list'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {
    AuthHelpers,
    useAuthHelper,
    useShopperCustomersMutation,
    useAccessToken,
} from '@salesforce/commerce-sdk-react'
import CustomToast from '../account/customToast'
import {ORDERS_HISTORY_SEARCH_PARAMS} from '../account/constant'
import {BRAND_KEY, BRAND_SHORT_LABEL} from '../../brand'
import WishlistWidget from './wishlist-widget'
import OrderWidget from './order-widget'
import RegisteredProductWidget from './registered-product/registered-product-widget'
import {useToast} from '../../hooks/use-toast'
import {getMrtApiBasepath} from '../../ssr/helpers'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCustomPreference} from '../../hooks/use-custom-preference'
import {useTokenHelper} from '../../utils/token-helper'
// import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
// import {Helmet} from 'react-helmet'
// import {useLocation} from 'react-router-dom'

/**
 * This is a specialized Skeleton component that which uses the customers authtype as the
 * `isLoaded` state. It also will revert it's provided size (height, width) when the loaded
 * state changes. This allows you to have skeletons of a specific size, but onece loaded
 * the bounding element will affect the contents size.
 */
// eslint-disable-next-line react/prop-types
const Skeleton = forwardRef(({children, height, width, ...rest}, ref) => {
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const size = !isRegistered
        ? {
              height,
              width,
          }
        : {}
    return (
        <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
            {children}
        </ChakraSkeleton>
    )
})

Skeleton.displayName = 'Skeleton'

const ProfileCard = ({isEditing, setIsEditing}) => {
    const {data: customer} = useCurrentCustomer()
    const updateCustomer = useShopperCustomersMutation('updateCustomer')
    // eslint-disable-next-line no-unused-vars
    const {isRegistered, customerId, email} = customer
    const styles = useMultiStyleConfig('Account')
    const {gigyaObject, gigyaLocale} = useGigya()
    const [showDeleteBlock, setShowDeleteBlock] = useState(false)
    const api = useCustomApi()
    const navigate = useNavigation()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const [isSocialLogin, setIsSocialLogin] = useState(false)
    const toast = useToast()
    const {formatDate, formatMessage} = useIntl()
    function syncBirthDateInputs() {
        const visibleInput = document.querySelectorAll('input[type="date"]')[1]
        const hiddenInput = document.querySelectorAll('input[name="data.birthDate"]')[1]

        if (!visibleInput || !hiddenInput) {
            return false
        }
        function handleChange() {
            hiddenInput.value = visibleInput.value
            hiddenInput.dispatchEvent(new Event('input', {bubbles: true}))
        }
        if (hiddenInput.value) {
            visibleInput.value = hiddenInput.value
            visibleInput.dispatchEvent(new Event('input', {bubbles: true}))
        }

        visibleInput.removeEventListener('change', handleChange)
        visibleInput.removeEventListener('input', handleChange)

        visibleInput.addEventListener('change', handleChange)
        visibleInput.addEventListener('input', handleChange)

        return true
    }

    const form = useForm({
        defaultValues: {
            firstName: customer?.firstName,
            lastName: customer?.lastName,
            email: customer?.email,
            phone: customer?.phoneHome,
        },
    })
    const isEditingRef = useRef(isEditing)
    useEffect(() => {
        isEditingRef.current = isEditing

        window.DLG = window?.DLG || {
            ...window.DLG,
            EVE: {
                emit: (e) => {
                    if (e === 'GIGYA.PROFILE.INFO.BACK') {
                        setIsEditing(false)
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                        })
                    }
                },
            },
            gigya: {brand: BRAND_KEY},
        }
    }, [isEditing])

    useEffect(() => {
        if (!gigyaObject || !isRegistered) return

        const screenSetConfig = {
            screenSet: 'Default-ProfileUpdate',
            deviceType: 'desktop',
            lang: gigyaLocale,
            onAfterScreenLoad: () => {
                const interval = setInterval(() => {
                    const synced = syncBirthDateInputs()
                    if (synced) {
                        clearInterval(interval)
                    }
                }, 50)
                const inputs = document.querySelectorAll('.gigya-input-text')
                inputs.forEach((input) => {
                    if (input.value) {
                        input
                            .closest('.dlg-gigya-input-text-wrapper')
                            .classList.add('focus-visible')
                    }
                })
            },
            onBeforeScreenLoad: (params) => {
                //TODO change how we check for social login user
                if (params?.profile?.photoURL || params?.profile?.thumbnailURL) {
                    setIsSocialLogin(true)
                }
            },

            onBeforeValidation: () => {},
            onError: () => {},
            onFieldChanged: () => {},
        }

        // Profile Update
        gigyaObject?.accounts.showScreenSet({
            ...screenSetConfig,
            containerID: 'profile-update',
            startScreen: 'gigya-update-profile-screen',
            onAfterSubmit: (params) => {
                if (params.response.status === 'OK') {
                    const profileData = {
                        firstName: params.profile.firstName,
                        lastName: params.profile.lastName,
                        email: params.profile.email,
                        phoneHome: params.data?.phones.home,
                        birthday: params.data?.birthDate,
                        gender: params.data?.gender,
                    }
                    updateCustomer
                        .mutateAsync({
                            body: profileData,
                            parameters: {customerId: customerId},
                        })
                        .then(() => {
                            toast({
                                title: formatMessage({
                                    defaultMessage: 'Profile updated successfully',
                                    id: 'profile_card.toast.update_profile',
                                }),
                                status: 'success',
                            })
                        })
                }
            },
        })

        // Password Update
        gigyaObject?.accounts.showScreenSet({
            ...screenSetConfig,
            containerID: 'password-update',
            startScreen: 'gigya-change-password-screen',
            onAfterSubmit: (params) => {
                if (params.response.status === 'OK') {
                    toast({
                        title: formatMessage({
                            defaultMessage: `Password updated successfully`,
                            id: 'profile_card.toast.change_password',
                        }),
                        status: 'success',
                    })
                }
            },
        })
    }, [gigyaObject, isEditing])

    useEffect(() => {
        form.reset({
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            phone: customer.phoneHome,
        })
    }, [customer?.firstName, customer?.lastName, customer?.email, customer?.phoneHome])

    const handleDeleteClick = () => {
        setShowDeleteBlock(!showDeleteBlock)
    }

    const handleConfirmDelete = async () => {
        try {
            await api.deleteCustomer(customer.c_gigyaId)
            gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
            await logout.mutateAsync()
            toast({
                title: formatMessage({
                    defaultMessage: `Account deleted successfully`,
                    id: 'profile_card.toast.delete_account',
                }),
                status: 'success',
            })
            const endpoint = origin + getMrtApiBasepath('/logout')
            await fetch(`${endpoint}`).then(() => {
                // Dopo che il browser ha ricevuto Clear-Site-Data
                setTimeout(() => {
                    navigate('/login') // Redirect pulito
                }, 500) // Un piccolo delay aiuta a garantire che la pulizia sia completata
            })
        } catch (error) {
            console.error('Errore durante l’eliminazione del customer:', error)
        }
    }

    return (
        <ToggleCard
            id="my-profile"
            editing={isEditing}
            isLoading={form.formState.isSubmitting}
            onEdit={isRegistered ? () => setIsEditing(true) : undefined}
            editLabel={<FormattedMessage defaultMessage="Edit" id="profile_card.button.edit" />}
            variant={isEditing ? 'edit-card' : 'profile-card'}
            layerStyle="cardBordered"
            {...styles.ToggleCardContainer}
            isAccount={true}
        >
            <ToggleCardSummary>
                <SimpleGrid columns={{base: 1, lg: 2}} spacing={4} w={'100%'}>
                    <Box data-cs-mask>
                        <Skeleton height={remCalc(21)} width={remCalc(84)} {...styles.fieldBox}>
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Name:"
                                    id="profile_card.label.name"
                                />
                            </Text>
                            <Text fontWeight="bold">{customer?.firstName}</Text>
                        </Skeleton>
                    </Box>
                    <Box data-cs-mask>
                        <Skeleton height={remCalc(21)} width={remCalc(84)} {...styles.fieldBox}>
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Last Name:"
                                    id="profile_card.label.last_name"
                                />
                            </Text>
                            <Text fontWeight="bold">{customer?.lastName}</Text>
                        </Skeleton>
                    </Box>
                    <Box data-cs-mask>
                        <Skeleton height={remCalc(21)} width={remCalc(84)} {...styles.fieldBox}>
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Date of Birth:"
                                    id="profile_card.label.dob"
                                />
                            </Text>
                            <Text fontWeight="bold">{formatDate(customer?.birthday)}</Text>
                        </Skeleton>
                    </Box>
                    <Box data-cs-mask>
                        <Skeleton
                            height={remCalc(21)}
                            width={remCalc(84)}
                            {...styles.emailFieldBox}
                        >
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Email:"
                                    id="profile_card.label.email"
                                />
                            </Text>
                            <Text fontWeight="bold">{customer?.email}</Text>
                        </Skeleton>
                    </Box>
                    <Box data-cs-mask>
                        <Skeleton height={remCalc(21)} width={remCalc(84)} {...styles.fieldBox}>
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Phone Number:"
                                    id="profile_card.label.phone"
                                />
                            </Text>
                            <Text fontWeight="bold">{customer?.phoneHome}</Text>
                        </Skeleton>
                    </Box>
                    <Box data-cs-mask>
                        <Skeleton height={remCalc(21)} width={remCalc(120)} {...styles.fieldBox}>
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Gender:"
                                    id="profile_card.label.gender"
                                />
                            </Text>
                            <Text fontWeight="bold">{customer?.gender}</Text>
                        </Skeleton>
                    </Box>
                </SimpleGrid>
            </ToggleCardSummary>
            <ToggleCardEdit>
                <Container variant="form" maxW={'100%'}>
                    <Stack
                        gap={{base: remCalc(18), lg: remCalc(24)}}
                        paddingBottom={{base: remCalc(18), lg: remCalc(24)}}
                        __css={styles}
                    >
                        <Text {...styles.formTitle}>
                            <FormattedMessage
                                defaultMessage="Personal Data"
                                id="profile_card.form.title.personal_data"
                            />
                        </Text>
                        <Text {...styles.formMandatory}>
                            <FormattedMessage
                                defaultMessage="*mandatory field"
                                id="profile_card.form.mandatory_field"
                            />
                        </Text>
                        <div id="profile-update"></div>
                    </Stack>
                    {!isSocialLogin && (
                        <Stack
                            gap={{base: remCalc(18), lg: remCalc(24)}}
                            paddingBottom={{base: remCalc(18), lg: remCalc(24)}}
                            __css={styles}
                        >
                            <Text {...styles.formTitle}>
                                <FormattedMessage
                                    defaultMessage="Change Password"
                                    id="profile_card.form.title.change_password"
                                />
                            </Text>
                            <Text {...styles.formMandatory}>
                                <FormattedMessage
                                    defaultMessage="*mandatory field"
                                    id="profile_card.form.mandatory_field"
                                />
                            </Text>
                            <div id="password-update"></div>
                        </Stack>
                    )}
                    <Button
                        borderTop={'small'}
                        borderStyle={'solid'}
                        borderColor={'border.light'}
                        {...styles.deleteButton}
                        variant="text-button"
                        onClick={handleDeleteClick}
                        aria-label={'delete-account'}
                        data-testid="delete-account-button"
                    >
                        <FormattedMessage
                            defaultMessage="Delete account"
                            id="profile_card.button.delete_account"
                        />
                    </Button>

                    {showDeleteBlock && (
                        <Box {...styles.deleteBlock}>
                            <Text {...styles.deleteBlockText}>
                                <FormattedMessage
                                    defaultMessage="Are you sure you want to delete your account? Deleting your account will remove all content and data associated with it. Account deletion is PERMANENT and IRREVERSIBLE. You will not be able to access your account immediately. REGISTERED PRODUCT: All content will be deleted. YOUR INFO: All your information"
                                    id="profile_card.delete_block.text"
                                />
                            </Text>
                            <Button
                                {...styles.confirmDeleteButton}
                                variant="primary"
                                onClick={() => handleConfirmDelete()}
                                aria-label={'confirm-delete-account'}
                            >
                                <FormattedMessage
                                    defaultMessage="Delete my account"
                                    id="profile_card.button.confirm_delete_account"
                                />
                            </Button>
                        </Box>
                    )}
                </Container>
            </ToggleCardEdit>
        </ToggleCard>
    )
}

ProfileCard.propTypes = {
    customToast: PropTypes.object,
    setCustomToast: PropTypes.func.isRequired,
    isEditing: PropTypes.bool,
    setIsEditing: PropTypes.func.isRequired,
}

const AccountDetail = () => {
    const {data: customer} = useCurrentCustomer()
    const styles = useMultiStyleConfig('Account')
    const employeeBalancePrefData = useCustomPreference('EmployeeProgram_Data')
    const headingRef = useRef()
    const [isEditing, setIsEditing] = useState(false)
    const [customToast, setCustomToast] = useState(null)
    const {data: wishListData, isLoading: isWishListLoading} = useWishList()
    const searchParams = useSearchParams(ORDERS_HISTORY_SEARCH_PARAMS)
    const {offset} = searchParams[0]
    const appOrigin = useOrigin(false)
    const [orders, setOrders] = useState([])
    // eslint-disable-next-line no-unused-vars
    const [isLoading, setIsLoading] = useState(true)
    // eslint-disable-next-line no-unused-vars
    const [error, setError] = useState(null)
    const {getTokenWhenReady} = useAccessToken()
    const {fetchOMSValidToken} = useTokenHelper()
    // const location = useLocation()
    // const {customParams} = getConfig()
    // const currentPath = 'https://' + customParams.currentHost + location.pathname
    // const webPageSchemaMarkup = {
    //     '@context': 'https://schema.org/',
    //     '@type': 'WebPage',
    //     name: 'Account Details',
    //     description: 'View details of your account',
    //     url: currentPath,
    // }

    useEffect(() => {
        const fetchOrders = async () => {
            setIsLoading(true)
            try {
                const accessToken = await fetchOMSValidToken()

                const queryParams = new URLSearchParams({
                    gigyaId: customer.c_gigyaId,
                    sfAccountEmail: customer.email,
                    brand: BRAND_SHORT_LABEL,
                    limit: 2,
                    offset,
                })
                const token = await getTokenWhenReady()
                // Use the token from getTokenWhenReady for Commerce Cloud APIs
                // and accessToken for OMS APIs

                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-order-list-oms?${queryParams.toString()}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessToken}`, // OMS token
                            customerId: customer?.customerId,
                            'x-commercecloud-token': `Bearer ${token}`, // Send Commerce Cloud token as a custom header
                        },
                    },
                )

                const result = await response.json()
                if (result.success) {
                    setOrders(result.orders)
                } else {
                    setError('Failed to fetch order list: ' + result.error)
                }
            } catch (error) {
                console.error('Error fetching order list:', error)
                setError('Error fetching order list: ' + error.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (customer?.email) {
            fetchOrders()
        }
    }, [customer?.email])

    useEffect(() => {
        // Focus the 'Account Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const handleGoBack = () => {
        setIsEditing(false)
    }

    const components = [
        {
            id: 'employeeSales',
            component: <EmployeeBalance />,
            priority: 1,
            available:
                customer?.c_isEmployee &&
                employeeBalancePrefData &&
                Object.keys(employeeBalancePrefData).length > 0,
        },
        {
            id: 'lastOrders',
            component: <OrderWidget orders={orders} />,
            priority: 2,
            available: orders.length > 0,
        },
        {
            id: 'wishlist',
            component: (
                <WishlistWidget wishListData={wishListData} isWishListLoading={isWishListLoading} />
            ),
            priority: 3,
            available: wishListData?.customerProductListItems?.length > 0,
        },
        {
            id: 'registerProduct',
            component: <RegisteredProductWidget />,
            priority: 4,
            available: true,
        },
    ]

    const availableComponents = components
        .filter((c) => c.available)
        .sort((a, b) => a.priority - b.priority)
        .slice(0, 2)

    return (
        <Stack
            data-testid="account-detail-page"
            data-gtm="account-detail-page"
            {...styles.accountDetailContainer}
        >
            {/* <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet> */}
            <Button
                variant="text-button"
                onClick={handleGoBack}
                {...styles.goBackButton}
                display={!isEditing && 'none'}
            >
                <FormattedMessage defaultMessage="Go Back" id="account_detail.button.go_back" />
            </Button>
            <Text as="h1" tabIndex="0" ref={headingRef} {...styles.accountTitle}>
                <FormattedMessage
                    defaultMessage="Account Details"
                    id="account_detail.title.account_details"
                />
            </Text>
            {customToast && (
                <CustomToast
                    message={customToast.message}
                    status={customToast.status}
                    onClose={() => setCustomToast(null)}
                    duration={5000}
                />
            )}
            <Stack {...styles.accountDetailContent}>
                <ProfileCard
                    handleGoBack={handleGoBack}
                    customToast={customToast}
                    setCustomToast={setCustomToast}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                />
                {availableComponents.map((comp) => (
                    <React.Fragment key={comp.id}>{comp.component}</React.Fragment>
                ))}
            </Stack>
        </Stack>
    )
}

AccountDetail.getTemplateName = () => 'account-detail'

export default AccountDetail
